import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogScrollBody, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Upload, FileSpreadsheet, Tag } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";
import {
  IMPORT_TARGETS,
  autoMapColumns,
  buildImportPreview,
  parseCsvFile,
  validateMapping,
} from "@/lib/csvImport";

export default function AudienceDetailPage() {
  const confirm = useConfirm();
  const { id } = useParams();
  const [audience, setAudience] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagFilter, setTagFilter] = useState("__all__");
  const [metaLoading, setMetaLoading] = useState(true);
  const [metaError, setMetaError] = useState(null);
  const [openAdd, setOpenAdd] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", tags: "" });
  const [bulk, setBulk] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState({});
  const [csvPreview, setCsvPreview] = useState(null);
  const [parsingCsv, setParsingCsv] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importJob, setImportJob] = useState(null);

  const fetchContacts = useCallback((page, limit) => {
    const params = { page, limit };
    if (tagFilter !== "__all__") params.tag = tagFilter;
    return api.get(`/audiences/${id}/contacts`, { params });
  }, [id, tagFilter]);

  const {
    items: contacts,
    total: contactTotal,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading: contactsLoading,
    reload: reloadContacts,
    resetPage,
  } = usePaginatedList(fetchContacts, [id, tagFilter]);

  const loadMeta = useCallback(async () => {
    setMetaLoading(true);
    setMetaError(null);
    try {
      const [a, t] = await Promise.all([
        api.get(`/audiences/${id}`),
        api.get(`/audiences/${id}/tags`),
      ]);
      setAudience(a.data);
      setTags((t.data.tags || []).filter((tag) => tag != null && String(tag).trim()).map(String));
    } catch (err) {
      setAudience(null);
      setMetaError(err?.response?.data?.detail || "Failed to load audience");
    } finally {
      setMetaLoading(false);
    }
  }, [id]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { resetPage(); }, [tagFilter, resetPage]);

  const pollImportJob = useCallback((jobId) => {
    const poll = async () => {
      try {
        const { data } = await api.get(`/jobs/${jobId}`);
        setImportJob(data);
        if (data.status === "completed") {
          const skipped = data.skipped ?? 0;
          toast.success(
            skipped
              ? `Import complete: ${data.added} added, ${skipped} skipped (duplicates in file or already in audience)`
              : `Import complete: ${data.added} added`,
          );
          setOpenBulk(false);
          setBulk("");
          setCsvFile(null);
          resetCsvImport();
          setImportJob(null);
          reloadContacts();
          loadMeta();
          return;
        }
        if (data.status === "failed") {
          toast.error(data.error || "Import failed");
          setImportJob(null);
          return;
        }
        if (data.status === "staging") {
          setImportJob(data);
          setTimeout(poll, 2000);
          return;
        }
        setTimeout(poll, 3000);
      } catch {
        setImportJob(null);
      }
    };
    poll();
  }, [reloadContacts, loadMeta]);

  const addContact = async () => {
    if (!form.email) return toast.error("Email is required");
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.post(`/audiences/${id}/contacts`, payload);
      toast.success("Contact added");
      setOpenAdd(false);
      setForm({ email: "", first_name: "", last_name: "", tags: "" });
      reloadContacts();
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add");
    }
  };

  const resetCsvImport = () => {
    setCsvFile(null);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvMapping({});
    setCsvPreview(null);
  };

  const refreshServerPreview = async (file, mapping) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("mapping", JSON.stringify(mapping));
    const { data } = await api.post(`/audiences/${id}/contacts/upload/preview`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const handleCsvFile = async (file) => {
    if (!file) {
      resetCsvImport();
      return;
    }
    setCsvFile(file);
    setParsingCsv(true);
    try {
      const { headers, rows } = await parseCsvFile(file);
      if (!headers.length) {
        toast.error("Could not read CSV headers");
        resetCsvImport();
        return;
      }
      const suggested = autoMapColumns(headers);
      const localPreview = buildImportPreview(headers, rows, suggested);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setCsvMapping(localPreview.mapping);
      try {
        const serverPreview = await refreshServerPreview(file, localPreview.mapping);
        setCsvPreview({ ...localPreview, ...serverPreview, sample: localPreview.sample });
      } catch {
        setCsvPreview(localPreview);
      }
    } catch {
      toast.error("Failed to parse CSV file");
      resetCsvImport();
    } finally {
      setParsingCsv(false);
    }
  };

  const updateCsvMapping = async (header, target) => {
    const next = { ...csvMapping, [header]: target };
    setCsvMapping(next);
    const localPreview = buildImportPreview(csvHeaders, csvRows, next);
    if (csvFile) {
      try {
        const serverPreview = await refreshServerPreview(csvFile, next);
        setCsvPreview({ ...localPreview, ...serverPreview, sample: localPreview.sample });
        return;
      } catch {
        /* fall back to local stats */
      }
    }
    setCsvPreview(localPreview);
  };

  const importBulk = async () => {
    const lines = bulk.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return toast.error("Paste at least one email");
    const list = lines.map((line) => {
      const [email, first_name, last_name, tagStr] = line.split(",").map((s) => (s || "").trim());
      return { email, first_name, last_name, tags: tagStr ? tagStr.split("|").map((t) => t.trim()).filter(Boolean) : [] };
    }).filter((c) => c.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.email));
    if (!list.length) return toast.error("No valid emails");
    try {
      const { data } = await api.post(`/audiences/${id}/contacts/bulk`, { contacts: list });
      if (data.background) {
        toast.success(data.message || "Import started in background");
        setImportJob(data);
        pollImportJob(data.job_id);
        return;
      }
      toast.success(`Imported ${data.added} (${data.skipped} skipped)`);
      setOpenBulk(false);
      setBulk("");
      reloadContacts();
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    }
  };

  const uploadCsv = async () => {
    if (!csvFile) return toast.error("Pick a CSV file");
    const err = validateMapping(csvMapping);
    if (err) return toast.error(err);
    if (!csvPreview?.willImport && !csvPreview?.uniqueEmails && !csvPreview?.total) return toast.error("No valid email rows to import");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      fd.append("mapping", JSON.stringify(csvMapping));
      const { data } = await api.post(`/audiences/${id}/contacts/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.background) {
        toast.success(data.message || "Import started in background");
        setImportJob(data);
        pollImportJob(data.job_id);
        return;
      }
      toast.success(`Imported ${data.added} (${data.skipped} skipped)`);
      resetCsvImport();
      setOpenBulk(false);
      reloadContacts();
      loadMeta();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const deleteContact = async (cid) => {
    const ok = await confirm({
      title: "Delete contact?",
      description: "Remove this contact from the audience?",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/contacts/${cid}`);
    toast.success("Contact deleted");
    reloadContacts();
    loadMeta();
  };

  if (metaLoading) return <div className="p-12 text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>;
  if (metaError || !audience) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <Link to="/audiences" className="inline-flex items-center text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to audiences
        </Link>
        <div className="lift-card bg-white dark:bg-neutral-950 p-8 rounded-sm text-center">
          <h3 className="font-display font-bold text-xl mb-2">Could not load audience</h3>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">{metaError || "Audience not found"}</p>
        </div>
      </div>
    );
  }

  const importCount = csvPreview?.willImport ?? csvPreview?.uniqueEmails ?? csvPreview?.total ?? 0;
  const canImportCsv = Boolean(csvFile && importCount);

  return (
    <div data-testid="audience-detail-page">
      <PageHeader
        title={audience.name}
        subtitle={audience.description || `${audience.contact_count} contacts`}
        action={
          <div className="flex gap-2">
            <Dialog open={openBulk} onOpenChange={(open) => { setOpenBulk(open); if (!open) resetCsvImport(); }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm h-11 font-semibold" data-testid="bulk-import-button">
                  <Upload className="h-4 w-4 mr-2" /> Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl flex flex-col max-h-[min(90dvh,calc(100vh-2rem))] overflow-hidden gap-0 p-0">
                <div className="px-6 pt-6 pb-4 shrink-0 space-y-4 border-b border-neutral-200 dark:border-neutral-800">
                  <DialogHeader className="text-left space-y-1.5">
                    <DialogTitle>Import contacts</DialogTitle>
                  </DialogHeader>
                  {importJob && (
                    <div className="rounded-sm border border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/40 px-4 py-3 text-sm text-blue-900 dark:text-blue-100">
                      <p className="font-semibold">Import in progress — you can close this dialog</p>
                      <p className="mt-1 text-blue-800 dark:text-blue-200">
                        {importJob.processed ?? 0} / {importJob.total ?? 0} processed
                        {" · "}{importJob.added ?? 0} added · {importJob.skipped ?? 0} skipped
                      </p>
                    </div>
                  )}
                </div>
                <DialogScrollBody className="px-6 py-4 space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileSpreadsheet className="h-4 w-4 text-neutral-700 dark:text-neutral-300" />
                      <Label className="text-xs font-bold uppercase tracking-wider mb-0">CSV upload</Label>
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                      Supports columns like <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">email</code>,{" "}
                      <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">name</code>,{" "}
                      <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">first_name</code>,{" "}
                      <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">last_name</code>,{" "}
                      <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">tags</code>,{" "}
                      <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">segments</code>.
                      Extra columns can be mapped before import.
                    </p>
                    <Input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={(e) => handleCsvFile(e.target.files?.[0] || null)}
                      className="rounded-sm"
                      data-testid="csv-file-input"
                    />
                    {parsingCsv && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2">Reading CSV…</p>}

                    {csvHeaders.length > 0 && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider">Map columns</Label>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1 mb-3">
                            Match each CSV column to a contact field. Unmapped columns are skipped.
                          </p>
                          <div className="space-y-2">
                            {csvHeaders.map((header) => (
                              <div key={header} className="grid grid-cols-2 gap-2 items-center">
                                <span className="text-sm font-medium truncate" title={header}>{header}</span>
                                <Select
                                  value={csvMapping[header] || "ignore"}
                                  onValueChange={(v) => updateCsvMapping(header, v)}
                                >
                                  <SelectTrigger className="rounded-sm h-9" data-testid={`csv-map-${header}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {IMPORT_TARGETS.map((t) => (
                                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ))}
                          </div>
                        </div>

                        {csvPreview?.sample?.length > 0 && (
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider">Preview</Label>
                            <div className="mt-2 mb-3 rounded-sm border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-900 dark:text-amber-100 space-y-1">
                              <p><strong>{csvPreview.totalRows?.toLocaleString() ?? csvPreview.total?.toLocaleString()}</strong> rows in file</p>
                              <p><strong>{csvPreview.uniqueEmails?.toLocaleString() ?? csvPreview.total?.toLocaleString()}</strong> unique emails</p>
                              {(csvPreview.duplicatesInFile ?? 0) > 0 && (
                                <p><strong>{csvPreview.duplicatesInFile.toLocaleString()}</strong> duplicate rows in file (will be skipped)</p>
                              )}
                              {(csvPreview.alreadyInAudience ?? 0) > 0 && (
                                <p><strong>{csvPreview.alreadyInAudience.toLocaleString()}</strong> already in this audience (will be skipped)</p>
                              )}
                              <p className="font-semibold pt-1 border-t border-amber-200 dark:border-amber-800">
                                {csvPreview.willImport?.toLocaleString() ?? csvPreview.uniqueEmails?.toLocaleString()} new contacts to import
                              </p>
                            </div>
                            <div className="rounded-sm border border-neutral-200 dark:border-neutral-800 overflow-hidden text-xs max-h-48 overflow-y-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="bg-neutral-50 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
                                    <th className="text-left px-3 py-2">Email</th>
                                    <th className="text-left px-3 py-2">First name</th>
                                    <th className="text-left px-3 py-2">Last name</th>
                                    <th className="text-left px-3 py-2">Tags</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {csvPreview.sample.map((row, i) => (
                                    <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                                      <td className="px-3 py-2">{row.email}</td>
                                      <td className="px-3 py-2">{row.first_name || "—"}</td>
                                      <td className="px-3 py-2">{row.last_name || "—"}</td>
                                      <td className="px-3 py-2">{(row.tags || []).join(", ") || "—"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                  <div className="border-t border-neutral-200 dark:border-neutral-800 pt-6">
                    <Label className="text-xs font-bold uppercase tracking-wider">Or paste below</Label>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 my-2">Format: <code className="bg-neutral-100 dark:bg-neutral-800 px-1 rounded-sm">email,first_name,last_name,tag1|tag2</code></p>
                    <Textarea rows={4} value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder="alice@example.com,Alice,Smith,vip|launch" className="rounded-sm font-mono text-sm" data-testid="bulk-import-textarea" />
                    <Button onClick={importBulk} className="mt-3 rounded-sm font-semibold" variant="outline" data-testid="bulk-import-submit">Import pasted</Button>
                  </div>
                </DialogScrollBody>
                {csvHeaders.length > 0 && (
                  <DialogFooter className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-800 shrink-0 bg-background sm:justify-stretch">
                    <Button
                      onClick={uploadCsv}
                      disabled={!canImportCsv || uploading || parsingCsv}
                      className="rounded-sm btn-brand font-semibold w-full sm:w-auto"
                      data-testid="csv-upload-submit"
                    >
                      <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading..." : `Import ${importCount.toLocaleString()} contacts`}
                    </Button>
                  </DialogFooter>
                )}
              </DialogContent>
            </Dialog>
            <Dialog open={openAdd} onOpenChange={setOpenAdd}>
              <DialogTrigger asChild>
                <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="add-contact-button">
                  <Plus className="h-4 w-4 mr-2" /> Add contact
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Add contact</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Email</Label>
                    <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 rounded-sm" data-testid="contact-email-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider">First name</Label>
                      <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-2 rounded-sm" data-testid="contact-first-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider">Last name</Label>
                      <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-2 rounded-sm" data-testid="contact-last-name-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider">Tags (comma-separated)</Label>
                    <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-2 rounded-sm" placeholder="vip, launch" data-testid="contact-tags-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenAdd(false)} className="rounded-sm">Cancel</Button>
                  <Button onClick={addContact} className="rounded-sm btn-brand font-semibold" data-testid="contact-add-submit">Add</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <Link to="/audiences" className="inline-flex items-center text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 mb-6">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to audiences
        </Link>

        {tags.length > 0 && (
          <div className="mb-4 flex items-center gap-3">
            <Tag className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
            <Select value={tagFilter} onValueChange={setTagFilter}>
              <SelectTrigger className="w-60 rounded-sm" data-testid="tag-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All tags</SelectItem>
                {tags.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-xs text-neutral-500 dark:text-neutral-400">{(contactTotal ?? 0).toLocaleString()} contact{contactTotal !== 1 ? "s" : ""}</span>
          </div>
        )}

        {contactsLoading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading contacts...</div>
        ) : contacts.length === 0 ? (
          <div className="lift-card bg-white dark:bg-neutral-950 p-12 rounded-sm text-center">
            <h3 className="font-display font-bold text-xl mb-2">No contacts {tagFilter !== "__all__" ? `with tag "${tagFilter}"` : "yet"}</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">Add contacts one-by-one or import via CSV.</p>
            <Button onClick={() => setOpenAdd(true)} className="rounded-sm btn-brand font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Add contact
            </Button>
          </div>
        ) : (
          <div className="lift-card bg-white dark:bg-neutral-950 rounded-sm overflow-hidden">
            <table className="w-full" data-testid="contacts-table">
              <thead>
                <tr className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
                  <th className="text-left px-6 py-3 label-overline">Email</th>
                  <th className="text-left px-6 py-3 label-overline">Name</th>
                  <th className="text-left px-6 py-3 label-overline">Tags</th>
                  <th className="text-left px-6 py-3 label-overline">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-900">
                    <td className="px-6 py-4 text-sm font-medium">{c.email}</td>
                    <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-400">{c.first_name} {c.last_name}</td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).filter(Boolean).map((t, ti) => (
                          <span key={`${c.id}-${t}-${ti}`} className="inline-flex items-center text-xs px-2 py-0.5 rounded-sm border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 font-medium">{String(t)}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{
                            backgroundColor:
                              c.status === "subscribed"
                                ? "var(--accent-green)"
                                : c.status === "suspended"
                                  ? "#F59E0B"
                                  : "#A3A3A3",
                          }}
                        />
                        {c.status}
                        {c.status === "suspended" && c.blocked_domain && (
                          <span className="text-neutral-400 font-normal">({c.blocked_domain})</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => deleteContact(c.id)} className="text-neutral-400 hover:text-red-600" data-testid={`contact-delete-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <ListPagination
              page={page}
              pages={pages}
              total={contactTotal}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </div>
        )}
      </div>
    </div>
  );
}
